import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CodedResponseModel } from 'src/app/model/CodedResponseModel';
import { CertificationForm } from 'src/app/model/responsible/CertificationForm';
import { LoaderComponent } from 'src/app/partials/loader/loader.component';
import { PartialService } from 'src/app/services/partial.service';
import { CertificationFormAPI } from 'src/app/services/responsible/certification-forms.service';

@Component({
    selector: 'app-form-list',
    templateUrl: './form-list.component.html',
    styleUrls: ['./form-list.component.scss']
})
export class FormListComponent implements OnInit {
    public forms: CertificationForm[] = [];
    public loaded: boolean = false;

    public showNewFormModal: boolean = false;
    public newFormName: string = '';
    public newFormDescription: string = '';
    public creating: boolean = false;

    public showEditModal: boolean = false;
    public editTarget?: CertificationForm;
    public editName: string = '';
    public editDescription: string = '';
    public saving: boolean = false;

    public showDeleteModal: boolean = false;
    public deleteTarget?: CertificationForm;

    constructor(
        private certFormApi: CertificationFormAPI,
        private router: Router,
        private comm: PartialService,
    ) {}

    ngOnInit(): void {
        this.loadForms();
    }

    private loadForms() {
        LoaderComponent.show.emit(true);
        this.certFormApi.index().subscribe(res => {
            let response = CodedResponseModel.decode(res);
            this.forms = [];
            for (let f of response) {
                this.forms.push(CertificationForm.create(f));
            }
            this.loaded = true;
            LoaderComponent.show.emit(false);
        }, err => {
            this.comm.notificator.emit({ type: 'error', message: err.error?.message ?? 'Failed to load forms', timeout: 5000 });
            LoaderComponent.show.emit(false);
        });
    }

    public openNewFormModal() {
        this.newFormName = '';
        this.newFormDescription = '';
        this.showNewFormModal = true;
    }

    public createForm() {
        if (!this.newFormName.trim()) return;
        this.creating = true;
        let newForm = new CertificationForm();
        newForm.name = this.newFormName.trim();
        newForm.description = this.newFormDescription.trim();
        this.certFormApi.create(newForm).subscribe(res => {
            let response = CodedResponseModel.decode(res);
            this.creating = false;
            this.showNewFormModal = false;
            this.router.navigate(['/forms', response.id]);
        }, err => {
            this.creating = false;
            this.comm.notificator.emit({ type: 'error', message: err.error?.message ?? 'Failed to create form', timeout: 5000 });
        });
    }

    public editForm(form: CertificationForm) {
        this.router.navigate(['/forms', form.id]);
    }

    public openEditModal(form: CertificationForm) {
        this.editTarget = form;
        this.editName = form.name;
        this.editDescription = form.description;
        this.showEditModal = true;
    }

    public cancelEdit() {
        this.editTarget = undefined;
        this.showEditModal = false;
    }

    public saveEdit() {
        if (!this.editTarget || !this.editName.trim()) return;
        this.saving = true;
        let updated = CertificationForm.create(this.editTarget.responsify());
        updated.name = this.editName.trim();
        updated.description = this.editDescription.trim();
        this.certFormApi.update(updated).subscribe(res => {
            this.editTarget!.name = updated.name;
            this.editTarget!.description = updated.description;
            this.saving = false;
            this.showEditModal = false;
            this.editTarget = undefined;
            this.comm.notificator.emit({ type: 'success', message: 'Form updated', timeout: 3000 });
        }, err => {
            this.saving = false;
            this.comm.notificator.emit({ type: 'error', message: err.error?.message ?? 'Failed to update form', timeout: 5000 });
        });
    }

    public confirmDelete(form: CertificationForm) {
        this.deleteTarget = form;
        this.showDeleteModal = true;
    }

    public cancelDelete() {
        this.deleteTarget = undefined;
        this.showDeleteModal = false;
    }

    public deleteForm() {
        if (!this.deleteTarget) return;
        // Soft-delete by deactivating
        let updated = CertificationForm.create(this.deleteTarget.responsify());
        updated.active = false;
        this.certFormApi.update(updated).subscribe(res => {
            this.forms = this.forms.filter(f => f.id !== this.deleteTarget!.id);
            this.deleteTarget = undefined;
            this.showDeleteModal = false;
            this.comm.notificator.emit({ type: 'success', message: 'Form deactivated', timeout: 3000 });
        }, err => {
            this.comm.notificator.emit({ type: 'error', message: err.error?.message ?? 'Failed to deactivate form', timeout: 5000 });
            this.showDeleteModal = false;
        });
    }
}
